import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner, Alert, Badge, Table, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dashboardService from '../services/dashboardService';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getAllStats();
        setStats(data);
      } catch (err) {
        console.error('Dashboard veri hatası:', err);
        setError('Veriler yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      'Yeni': 'primary',
      'İşlemde': 'warning',
      'Çözüldü': 'success',
      'Kapatıldı': 'secondary',
      'Beklemede': 'info'
    };
    return colors[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Yönetim konsolu yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="dashboard-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Yönetim Konsolu</h2>
        <Badge bg="info" className="fs-6">
          Son Güncelleme: {new Date().toLocaleString('tr-TR')}
        </Badge>
      </div>

      {/* Ana İstatistik Kartları */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-primary">
            <Card.Body className="text-center">
              <div className="display-4 text-primary mb-2">{stats?.users?.total || 0}</div>
              <Card.Title>Toplam Kullanıcı</Card.Title>
              <Link to="/user-management" className="btn btn-outline-primary btn-sm mt-2">
                Detay Görüntüle
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-warning">
            <Card.Body className="text-center">
              <div className="display-4 text-warning mb-2">{stats?.tickets?.total || 0}</div>
              <Card.Title>Destek Talepleri</Card.Title>
              <Link to="/tickets" className="btn btn-outline-warning btn-sm mt-2">
                Detay Görüntüle
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-success">
            <Card.Body className="text-center">
              <div className="display-4 text-success mb-2">{stats?.questions?.total || 0}</div>
              <Card.Title>Toplam Soru</Card.Title>
              <Link to="/questions" className="btn btn-outline-success btn-sm mt-2">
                Detay Görüntüle
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-info">
            <Card.Body className="text-center">
              <div className="display-4 text-info mb-2">{stats?.memberships?.total || 0}</div>
              <Card.Title>Üyelik Paketleri</Card.Title>
              <Link to="/membership-packages" className="btn btn-outline-info btn-sm mt-2">
                Detay Görüntüle
              </Link>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* İkinci Satır - Detaylı İstatistikler */}
      <Row className="mb-4">
        {/* Destek Talepleri Durumu */}
        <Col lg={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-warning text-dark">
              <strong>Destek Talepleri Durumu</strong>
            </Card.Header>
            <Card.Body>
              {stats?.tickets?.byStatus && Object.keys(stats.tickets.byStatus).length > 0 ? (
                <>
                  {Object.entries(stats.tickets.byStatus).map(([status, count]) => {
                    const percentage = stats.tickets.total > 0
                      ? Math.round((count / stats.tickets.total) * 100)
                      : 0;
                    return (
                      <div key={status} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span>
                            <Badge bg={getStatusColor(status)} className="me-2">{status}</Badge>
                          </span>
                          <span>{count} talep ({percentage}%)</span>
                        </div>
                        <ProgressBar
                          variant={getStatusColor(status)}
                          now={percentage}
                          style={{ height: '10px' }}
                        />
                      </div>
                    );
                  })}
                </>
              ) : (
                <p className="text-muted text-center">Henüz destek talebi bulunmuyor.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Son Destek Talepleri */}
        <Col lg={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-primary text-white">
              <strong>Son Destek Talepleri</strong>
            </Card.Header>
            <Card.Body style={{ maxHeight: '300px', overflow: 'auto' }}>
              {stats?.tickets?.tickets && stats.tickets.tickets.length > 0 ? (
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>Açıklama</th>
                      <th>Durum</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.tickets.tickets.slice(0, 5).map((ticket) => (
                      <tr key={ticket.id}>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.description}
                        </td>
                        <td>
                          <Badge bg={getStatusColor(ticket.ticketStatusDto?.name)}>
                            {ticket.ticketStatusDto?.name || '-'}
                          </Badge>
                        </td>
                        <td>{new Date(ticket.createTime).toLocaleDateString('tr-TR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted text-center">Henüz destek talebi bulunmuyor.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Üçüncü Satır - Diğer İstatistikler */}
      <Row className="mb-4">
        {/* Etiketler */}
        <Col md={4} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-secondary text-white">
              <strong>Etiket Sistemi</strong>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-around text-center">
                <div>
                  <div className="h3 text-secondary">{stats?.tags?.totalTypes || 0}</div>
                  <small className="text-muted">Etiket Tipi</small>
                </div>
                <div className="border-start"></div>
                <div>
                  <div className="h3 text-secondary">{stats?.tags?.totalTags || 0}</div>
                  <small className="text-muted">Toplam Etiket</small>
                </div>
              </div>
              <div className="mt-3 d-flex gap-2">
                <Link to="/tag-types" className="btn btn-outline-secondary btn-sm flex-fill">
                  Tipler
                </Link>
                <Link to="/tags" className="btn btn-outline-secondary btn-sm flex-fill">
                  Etiketler
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Roller */}
        <Col md={4} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-dark text-white">
              <strong>Rol Yönetimi</strong>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-around text-center">
                <div>
                  <div className="h3 text-dark">{stats?.roles?.totalRoles || 0}</div>
                  <small className="text-muted">Rol</small>
                </div>
                <div className="border-start"></div>
                <div>
                  <div className="h3 text-dark">{stats?.roles?.totalGroups || 0}</div>
                  <small className="text-muted">Rol Grubu</small>
                </div>
              </div>
              <div className="mt-3 d-flex gap-2">
                <Link to="/roles" className="btn btn-outline-dark btn-sm flex-fill">
                  Roller
                </Link>
                <Link to="/role-groups" className="btn btn-outline-dark btn-sm flex-fill">
                  Gruplar
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Lokasyonlar */}
        <Col md={4} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-success text-white">
              <strong>Lokasyon Verileri</strong>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-around text-center">
                <div>
                  <div className="h3 text-success">{stats?.locations?.totalCountries || 0}</div>
                  <small className="text-muted">Ülke</small>
                </div>
                <div className="border-start"></div>
                <div>
                  <div className="h3 text-success">{stats?.locations?.totalCities || 0}</div>
                  <small className="text-muted">Şehir</small>
                </div>
              </div>
              <div className="mt-3 d-flex gap-2">
                <Link to="/countries" className="btn btn-outline-success btn-sm flex-fill">
                  Ülkeler
                </Link>
                <Link to="/cities" className="btn btn-outline-success btn-sm flex-fill">
                  Şehirler
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Dördüncü Satır - Üyelik Paketleri ve Sözleşmeler */}
      <Row className="mb-4">
        {/* Üyelik Paketleri Listesi */}
        <Col lg={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-info text-white">
              <strong>Üyelik Paketleri</strong>
            </Card.Header>
            <Card.Body>
              {stats?.memberships?.packages && stats.memberships.packages.length > 0 ? (
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>Paket Adı</th>
                      <th>Fiyat</th>
                      <th>Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.memberships.packages.slice(0, 5).map((pkg) => (
                      <tr key={pkg.id}>
                        <td><strong>{pkg.name}</strong></td>
                        <td>
                          <Badge bg="success">
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(pkg.price)}
                          </Badge>
                        </td>
                        <td>{pkg.durationInDays} gün</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted text-center">Henüz üyelik paketi bulunmuyor.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Soru ve Sözleşme Bilgileri */}
        <Col lg={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-purple" style={{ backgroundColor: '#6f42c1', color: 'white' }}>
              <strong>İçerik Yönetimi</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col sm={6} className="text-center mb-3">
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <div className="h2" style={{ color: '#6f42c1' }}>{stats?.questions?.total || 0}</div>
                      <p className="mb-0 text-muted">Soru</p>
                      <small className="text-muted">{stats?.questions?.typeCount || 0} tip</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} className="text-center mb-3">
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <div className="h2" style={{ color: '#6f42c1' }}>{stats?.agreements?.total || 0}</div>
                      <p className="mb-0 text-muted">Sözleşme</p>
                      <Link to="/agreements" className="text-decoration-none">
                        <small>Görüntüle</small>
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <div className="mt-2 d-flex gap-2">
                <Link to="/questions" className="btn btn-sm flex-fill" style={{ backgroundColor: '#6f42c1', color: 'white' }}>
                  Soruları Yönet
                </Link>
                <Link to="/question-types" className="btn btn-outline-secondary btn-sm flex-fill">
                  Soru Tipleri
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Hızlı Erişim Linkleri */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <strong>Hızlı Erişim</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
              <Link to="/user-management" className="btn btn-outline-primary w-100">
                Kullanıcılar
              </Link>
            </Col>
            <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
              <Link to="/tickets" className="btn btn-outline-warning w-100">
                Destek
              </Link>
            </Col>
            <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
              <Link to="/questions" className="btn btn-outline-success w-100">
                Sorular
              </Link>
            </Col>
            <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
              <Link to="/membership-packages" className="btn btn-outline-info w-100">
                Paketler
              </Link>
            </Col>
            <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
              <Link to="/tags" className="btn btn-outline-secondary w-100">
                Etiketler
              </Link>
            </Col>
            <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
              <Link to="/culture-texts" className="btn btn-outline-dark w-100">
                Kelimeler
              </Link>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Dashboard;
